import { useEffect, useReducer, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient, type QueryObserverResult } from '@tanstack/react-query';
import type { BrandProfileView, BrandWizardStep, Tenant } from '@mg/shared';
import { useUnsavedChangesGuard } from '../../components/settings/useUnsavedChangesGuard';
import { useToast } from '../../components/ui/toast';
import { ApiRequestError, actionErrorText, api, isBrandVersionConflict } from '../../lib/api';
import { useIsoLayoutEffect } from '../../lib/useIsoLayoutEffect';
import { BRAND_TOASTS, VERSION_CONFLICT_COPY } from '../brand/brandCopy';
import { REREAD_ENABLED } from '../brand/flags/reread';
import { URL_SCAN_ENABLED } from '../brand/flags/urlScan';
import { brandLeaveBlocks } from '../brand/leaveRules';
import { editPagePath } from '../brand/paths';
import { adoptBrandOnboarding, adoptBrandProfile, brandKeys } from '../brand/queries';
import {
  brandDraftReducer,
  initialBrandDraft,
  stepHasUserChanges,
  stepIsValid,
  stepPayload,
  type BrandDraft,
  type StepTarget,
} from './draft/brandDraft';
import type { ExitAction } from './ExitOnboardingDialog';
import { useImportJob, type ImportJobState } from './import/useImportJob';
import { useRevisitController } from './revisit/useRevisitController';
import { documentFileProblem, uploadErrorText } from './step1/fileCheck';
import type { RevisitChoice } from './step1/StartStep';
import { scanUrlsFromRows, URL_BOX_COPY, urlRowId, type ScanUrlsResult } from './step1/UrlListBox';
import { asWizardStep, isLastStep, stepAfter, stepBefore, type WizardMode } from './steps';
import type { BarAction, StepErrorState } from './WizardBar';
import { EXIT_DIALOG_COPY, POSITION_SAVE_FAILED, RELOAD_FAILED, STEP_SAVE_FAILED, WIZARD_UNSAVED_MESSAGE } from './wizardCopy';

/** Leaving the step by the bar or a pill, which asks first when the step has changes. */
export type LeaveIntent = { kind: 'back' } | { kind: 'skip' } | { kind: 'jump'; to: BrandWizardStep };

/** `file`: the document box shows the error. `focusId`: Next puts the focus there (a web-page row it refused). */
type SaveOutcome = { ok: true } | { ok: false; error: StepErrorState; file?: boolean; focusId?: string };

/** No web pages: the start choice is not Upload and prefill, or scanning is off (flags/urlScan.ts). */
const NO_PAGES: ScanUrlsResult = { ok: true, urls: [] };

/**
 * What a refused step save says (final-design §2.14): a version conflict offers Reload; any other 400,
 * 403 or 409 shows the server's own sentence; anything else the general one.
 */
function stepSaveError(err: unknown): StepErrorState {
  if (isBrandVersionConflict(err)) return { message: VERSION_CONFLICT_COPY.message, conflict: true };
  if (err instanceof ApiRequestError && [400, 401, 403, 409].includes(err.status) && err.message) {
    return { message: actionErrorText(err, STEP_SAVE_FAILED), conflict: false };
  }
  return { message: STEP_SAVE_FAILED, conflict: false };
}

function positionError(err: unknown): string {
  if (err instanceof ApiRequestError && [400, 401, 403].includes(err.status) && err.message) {
    return actionErrorText(err, POSITION_SAVE_FAILED);
  }
  return POSITION_SAVE_FAILED;
}

export interface WizardControllerOptions {
  tenant: Tenant;
  /** The brand's details as the wizard opened them. */
  initialView: BrandProfileView;
  /** Reads the brand's details again (Reload after a version conflict). */
  reloadProfile: () => Promise<QueryObserverResult<BrandProfileView, Error>>;
}

/**
 * Brand Setup's state machine (web.md §4.3, with final-design §2.14's `resumeAt`/`finish`):
 *
 * | Event | Server | Then |
 * |---|---|---|
 * | Next, step 1 (setup) | upload a chosen document and the web pages typed (starting one import), then save the start choice | step 2 |
 * | Next, step 1 (revisit) | nothing | step 2 |
 * | Next, steps 2-7 | save the step, `resumeAt` the next | next step, "Step saved" |
 * | Finish setup (8) | save with `finish` | Edit page, "Brand Setup completed" |
 * | Skip for now | the position (setup only) | next step |
 * | Back, a pill | nothing | that step |
 * | Exit, Save and exit | save the step, `resumeAt` this step | Edit page |
 * | Exit, Exit without saving | the position (setup only) | Edit page |
 *
 * Skip, Back and a pill ask first when the step has the person's own changes (the Unsaved changes
 * dialog: Discard, or Save and continue). Leaving the page asks the same way (R1's guard, which also
 * holds a workspace switch: brandLeaveBlocks). The wizard goes to the Edit page only once the step has
 * no unsaved changes, so the guard never asks about a step that was just saved or discarded.
 */
export function useWizardController({ tenant, initialView, reloadProfile }: WizardControllerOptions) {
  const tenantId = tenant.id;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const revisit = useRevisitController();

  // Fixed for the visit: no URL parameter, so a stale link cannot open the wrong mode.
  const [mode] = useState<WizardMode>(() => (initialView.onboarding.status === 'FINISHED' ? 'revisit' : 'setup'));
  const [state, dispatch] = useReducer(brandDraftReducer, initialView, initialBrandDraft);
  const [step, setStep] = useState<BrandWizardStep>(() =>
    initialView.onboarding.status === 'FINISHED' ? 1 : asWizardStep(initialView.onboarding.currentStep),
  );

  const [barBusy, setBarBusy] = useState<BarAction | null>(null);
  const [exitBusy, setExitBusy] = useState<ExitAction | null>(null);
  const [leaveBusy, setLeaveBusy] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [stepError, setStepError] = useState<StepErrorState | null>(null);
  const [leaveIntent, setLeaveIntent] = useState<LeaveIntent | null>(null);
  const [exitOpen, setExitOpen] = useState(false);
  const [exitError, setExitError] = useState<string | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [urls, setUrls] = useState<string[]>([]);
  const [revisitChoice, setRevisitChoice] = useState<RevisitChoice>('manual');

  const busy = barBusy !== null || exitBusy !== null || leaveBusy || reloading;

  // The draft as it is now, for the steps of a flow that run after an await.
  const latest = useRef<BrandDraft>(state);
  useIsoLayoutEffect(() => {
    latest.current = state;
  });

  // ── The import (setup only; merged while step 1's choice is Upload and prefill) ──
  const importOn = mode === 'setup' && state.startMode === 'UPLOAD';
  const imports: ImportJobState = useImportJob(tenantId, { enabled: mode === 'setup' });
  const job = imports.job;

  // The job's offers fill the draft as they arrive, again on every step, and after the choice changes
  // back to Upload and prefill (web.md §5.3). Merging the same job twice changes nothing.
  useEffect(() => {
    if (importOn && job) dispatch({ type: 'applyImport', job });
  }, [importOn, job, step]);

  const dirty = stepHasUserChanges(state, step);
  const check = stepIsValid(state, step);

  // ── Moving ──
  const focusField = (id: string) => {
    requestAnimationFrame(() => document.getElementById(id)?.focus());
  };

  const goToStep = (next: BrandWizardStep) => {
    // Undo after a copy lasts until the person moves on from the step (spec:153).
    dispatch({ type: 'dropCopyUndo' });
    setStepError(null);
    setStep(next);
  };

  /** Back to the saved values, with the import's offers back in place (they are not the person's). */
  const discardStep = (n: BrandWizardStep) => {
    dispatch({ type: 'discardStep', step: n });
    if (importOn && job) dispatch({ type: 'applyImport', job });
  };

  const leaveToEdit = () => {
    void queryClient.invalidateQueries({ queryKey: brandKeys.profile(tenantId) });
    setPendingNavigation(editPagePath('brand', tenant.slug));
  };

  // Navigates once the step has nothing unsaved, so the guard does not ask about what was just saved or
  // discarded (in the same tick it would still see the old state).
  useEffect(() => {
    if (pendingNavigation === null || dirty) return;
    setPendingNavigation(null);
    navigate(pendingNavigation);
  }, [pendingNavigation, dirty, navigate]);

  // ── Saving ──
  const saveStep = async (n: BrandWizardStep, goTo: StepTarget, importJobId?: string): Promise<SaveOutcome> => {
    const payload = stepPayload(latest.current, n, goTo);
    if (importJobId) payload.importJobId = importJobId;
    try {
      const view = await api.saveOnboardingStep(tenantId, n, payload);
      // The new version, or the next save is a 409 against itself.
      adoptBrandProfile(queryClient, view);
      dispatch({ type: 'stepSaved', step: n, view });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: stepSaveError(err) };
    }
  };

  /**
   * Step 1 in setup: uploads a chosen document and names the web pages typed in the box (Release 4), which
   * starts reading them all in one import (spec:358), then saves the start choice. A web-page row that is
   * not a public web address stops here, so no typed address is dropped without a word.
   */
  const saveStart = async (goTo: StepTarget): Promise<SaveOutcome> => {
    let importJobId: string | undefined;
    const upload = latest.current.startMode === 'UPLOAD';
    const pages = upload && URL_SCAN_ENABLED ? scanUrlsFromRows(urls) : NO_PAGES;
    if (!pages.ok) {
      return { ok: false, error: { message: pages.message, conflict: false }, focusId: urlRowId(pages.firstInvalid) };
    }
    if (upload && (pendingFile || pages.urls.length > 0)) {
      if (pendingFile) {
        const problem = documentFileProblem(pendingFile);
        if (problem) return { ok: false, error: { message: problem, conflict: false }, file: true };
      }
      setFileError(null);
      // The document box shows the upload's progress; web pages alone send no file.
      setUploading(pendingFile !== null);
      setUploadProgress(null);
      try {
        const started = await api.startBrandImport(
          tenantId,
          { file: pendingFile ?? undefined, urls: pages.urls },
          { onProgress: setUploadProgress },
        );
        importJobId = started.jobId;
      } catch (err) {
        if (pendingFile) return { ok: false, error: { message: uploadErrorText(err), conflict: false }, file: true };
        return { ok: false, error: { message: actionErrorText(err, URL_BOX_COPY.startFailed), conflict: false } };
      } finally {
        setUploading(false);
        setUploadProgress(null);
      }
      setPendingFile(null);
      setUrls([]);
      imports.started(importJobId);
    }
    return saveStep(1, goTo, importJobId);
  };

  // ── The bar ──
  const next = async () => {
    if (busy) return;
    setStepError(null);
    if (step === 1 && mode === 'revisit') {
      if (REREAD_ENABLED && revisitChoice === 'reread' && revisit.onStart) {
        setBarBusy('next');
        try {
          await revisit.onStart();
        } catch {
          setBarBusy(null);
          return;
        }
        setBarBusy(null);
      }
      goToStep(2);
      return;
    }
    if (step === 1) {
      setBarBusy('next');
      const result = await saveStart(2);
      setBarBusy(null);
      if (!result.ok) {
        if (result.file) setFileError(result.error.message);
        else setStepError(result.error);
        if (result.focusId) focusField(result.focusId);
        return;
      }
      toast.show(BRAND_TOASTS.stepSaved);
      goToStep(2);
      return;
    }
    if (!check.ok) {
      focusField(check.firstInvalid);
      return;
    }
    const last = isLastStep(step);
    setBarBusy(last ? 'finish' : 'next');
    const result = await saveStep(step, last ? 'finish' : stepAfter(step));
    setBarBusy(null);
    if (!result.ok) {
      setStepError(result.error);
      return;
    }
    if (last) {
      // No completion screen (spec:144): back to Edit Brand Workspace, where the toast shows.
      toast.show(BRAND_TOASTS.setupCompleted);
      leaveToEdit();
      return;
    }
    toast.show(BRAND_TOASTS.stepSaved);
    goToStep(stepAfter(step));
  };

  const targetOf = (intent: LeaveIntent): BrandWizardStep =>
    intent.kind === 'back' ? stepBefore(step) : intent.kind === 'skip' ? stepAfter(step) : intent.to;

  /** Leaves the step for `intent`'s target: after saving it (Save and continue), or dropping its changes. */
  const leave = async (intent: LeaveIntent, how: 'save' | 'discard') => {
    const target = targetOf(intent);
    if (how === 'save') {
      const valid = stepIsValid(latest.current, step);
      if (!valid.ok) {
        setLeaveIntent(null);
        focusField(valid.firstInvalid);
        return;
      }
      setLeaveBusy(true);
      // Skip's "Save and continue" is Next: Continue then reopens at the step after.
      const result = await saveStep(step, target);
      setLeaveBusy(false);
      setLeaveIntent(null);
      if (!result.ok) {
        setStepError(result.error);
        return;
      }
      toast.show(BRAND_TOASTS.stepSaved);
      goToStep(target);
      return;
    }
    setLeaveIntent(null);
    if (intent.kind === 'skip' && mode === 'setup') {
      // Skip for now records where Brand Setup reopens; it saves no field.
      setBarBusy('skip');
      try {
        const onboarding = await api.saveOnboardingPosition(tenantId, { step: target });
        adoptBrandOnboarding(queryClient, tenantId, onboarding);
      } catch (err) {
        setBarBusy(null);
        setStepError({ message: positionError(err), conflict: false });
        return;
      }
      setBarBusy(null);
    }
    if (stepHasUserChanges(latest.current, step)) discardStep(step);
    goToStep(target);
  };

  // The control that asked to leave, where Keep editing puts the focus back.
  const leaveOpener = useRef<HTMLElement | null>(null);

  const requestLeave = (intent: LeaveIntent) => {
    if (busy) return;
    if (stepHasUserChanges(state, step)) {
      leaveOpener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setLeaveIntent(intent);
      return;
    }
    void leave(intent, 'discard');
  };

  // ── Exit onboarding ──
  const exitOpener = useRef<HTMLElement | null>(null);

  const openExit = () => {
    if (busy) return;
    exitOpener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setExitError(null);
    setExitOpen(true);
  };

  const closeExit = () => {
    if (exitBusy !== null) return;
    setExitOpen(false);
    setExitError(null);
  };

  const exitFailed = (error: StepErrorState) => {
    if (error.conflict) {
      // Reload lives in the bar.
      setExitOpen(false);
      setStepError(error);
    } else {
      setExitError(error.message);
    }
  };

  const saveAndExit = async () => {
    if (exitBusy !== null) return;
    setExitError(null);
    if (step === 1 && mode === 'revisit') {
      setExitOpen(false);
      leaveToEdit();
      return;
    }
    if (step !== 1) {
      const valid = stepIsValid(latest.current, step);
      if (!valid.ok) {
        setExitError(EXIT_DIALOG_COPY.invalid);
        return;
      }
    }
    setExitBusy('save');
    const result = step === 1 ? await saveStart('stay') : await saveStep(step, 'stay');
    setExitBusy(null);
    if (!result.ok) {
      exitFailed(result.error);
      return;
    }
    toast.show(BRAND_TOASTS.stepSaved);
    setExitOpen(false);
    leaveToEdit();
  };

  const exitWithoutSaving = async () => {
    if (exitBusy !== null) return;
    setExitError(null);
    if (mode === 'setup') {
      // Continue Brand Setup reopens at this step (spec:146); nothing of it is saved.
      setExitBusy('discard');
      try {
        const onboarding = await api.saveOnboardingPosition(tenantId, { step });
        adoptBrandOnboarding(queryClient, tenantId, onboarding);
      } catch (err) {
        setExitBusy(null);
        setExitError(positionError(err));
        return;
      }
      setExitBusy(null);
    }
    dispatch({ type: 'discardStep', step });
    setPendingFile(null);
    setExitOpen(false);
    leaveToEdit();
  };

  // ── Reload after a version conflict: the server's values; this step's unsaved changes are gone ──
  const reload = async () => {
    if (busy) return;
    setReloading(true);
    try {
      const result = await reloadProfile();
      if (result.data && !result.isError) {
        dispatch({ type: 'load', view: result.data });
        if (importOn && job) dispatch({ type: 'applyImport', job });
        setStepError(null);
      } else {
        setStepError({ message: RELOAD_FAILED, conflict: true });
      }
    } finally {
      setReloading(false);
    }
  };

  // ── Step 1's document ──
  const chooseFile = (file: File) => {
    const problem = documentFileProblem(file);
    setFileError(problem);
    if (!problem) setPendingFile(file);
  };

  const removeFile = () => {
    setPendingFile(null);
    setFileError(null);
  };

  // ── Leaving the page with changes (R1's guard; also a workspace switch) ──
  const guard: ReactNode = useUnsavedChangesGuard(dirty, {
    save: async () => {
      const valid = stepIsValid(latest.current, step);
      if (!valid.ok) {
        focusField(valid.firstInvalid);
        return false;
      }
      setLeaveBusy(true);
      const result = await saveStep(step, 'stay');
      setLeaveBusy(false);
      if (!result.ok) {
        setStepError(result.error);
        return false;
      }
      toast.show(BRAND_TOASTS.stepSaved);
      return true;
    },
    discard: () => dispatch({ type: 'discardStep', step }),
    canSave: check.ok,
    saving: busy,
    message: WIZARD_UNSAVED_MESSAGE,
    shouldBlock: brandLeaveBlocks,
  });

  return {
    mode,
    step,
    state,
    dispatch,
    busy,
    barBusy,
    exitBusy,
    leaveBusy,
    stepError,
    check,
    importOn,
    imports,
    // step 1
    pendingFile,
    fileError,
    uploading,
    uploadProgress,
    urls,
    setUrls,
    revisitChoice,
    setRevisitChoice,
    chooseFile,
    removeFile,
    // the bar and the pills
    next: () => void next(),
    back: () => requestLeave({ kind: 'back' }),
    skip: () => requestLeave({ kind: 'skip' }),
    jump: (to: BrandWizardStep) => requestLeave({ kind: 'jump', to }),
    reload: () => void reload(),
    // the Unsaved changes dialog (Skip, Back, a pill)
    leaveIntent,
    leaveOpener,
    keepEditing: () => setLeaveIntent(null),
    leaveDiscarding: () => {
      if (leaveIntent) void leave(leaveIntent, 'discard');
    },
    leaveSaving: async () => {
      if (leaveIntent) await leave(leaveIntent, 'save');
    },
    // Exit onboarding
    exitOpen,
    exitError,
    exitOpener,
    openExit,
    closeExit,
    saveAndExit: () => void saveAndExit(),
    exitWithoutSaving: () => void exitWithoutSaving(),
    guard,
  };
}

export type WizardController = ReturnType<typeof useWizardController>;
